import { useCurrencyFormatter } from '@mint-lib/formatting-hooks';
import { useTranslation } from '@mint-lib/i18n';
import { Calendar, Information, Money } from '@mint-lib/icons';
import { Chip, Divider, typographyVariants } from '@mint-lib/tw-components';
import { cn } from '@mint-lib/tw-utils';

import { useMediaPlanSummaryApi } from '../../../../../api/queries/useMediaPlanSummaryApi.js';
import { BaseSummarySkeleton } from './BaseSkeleton.jsx';
import LevelDateRange from './LevelDateRange.jsx';

export const MediaPlanSummary = () => {
  const { t } = useTranslation('@myn/planning_v2');

  const { data: mediaPlanSummary, isPending } = useMediaPlanSummaryApi();

  const currencyFormatter = useCurrencyFormatter();

  const formatBudget = (budget: string) => {
    return currencyFormatter.format(Number(budget), {
      currency: mediaPlanSummary?.currency ?? 'USD',
    })?.formatted;
  };

  if (isPending || !mediaPlanSummary?.dateFrom || !mediaPlanSummary?.dateTo) {
    return <BaseSummarySkeleton />;
  }

  const bodyShort01 = typographyVariants({ variant: 'bodyShort01' });
  const bodyShort02 = typographyVariants({ variant: 'bodyShort02' });

  return (
    <div className="flex flex-col gap-3">
      <div className="flex gap-1">
        <Chip variant="outline" color="silver">
          <Information />
          <span>{t('Media Plan')}</span>
        </Chip>
        <Chip variant="outline" color="silver">
          <Calendar />
          <LevelDateRange
            dateFrom={mediaPlanSummary.dateFrom}
            dateTo={mediaPlanSummary.dateTo}
          />
        </Chip>
      </div>
      <h1 className={typographyVariants({ variant: 'h3' })}>
        {mediaPlanSummary?.name}
      </h1>
      <div className="flex items-center gap-2">
        <Money />
        <div className="flex">
          <span className={cn(bodyShort02, 'mr-1')}>
            {t('Planned budget for Media plan:')}
          </span>
          <span className={bodyShort02}>
            {formatBudget(mediaPlanSummary.plannedBudget)}
          </span>
        </div>
        <Divider orientation="vertical" className="mx-1 self-stretch" />
        <div className="flex">
          <span className={cn(bodyShort01, 'mr-1')}>
            {t('Planned in Activities:')}
          </span>
          <span className={bodyShort02}>
            {formatBudget(mediaPlanSummary.plannedActivitiesBudget)}
          </span>
        </div>
        <div className="ml-4 flex">
          <span className={cn(bodyShort01, 'mr-1')}>
            {t('Unplanned in Activities:')}
          </span>
          <span className={bodyShort02}>
            {formatBudget(mediaPlanSummary.unplannedActivitiesBudget)}
          </span>
        </div>
      </div>
    </div>
  );
};
